import { useEffect, useMemo, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { fetchCustomers, type Customer } from '@/services/customers';
import { fetchVariants, type VariantOption } from '@/services/variants';
import { createDirectSale } from '@/services/sales';
import { useShippingConfig } from '@/hooks/useShippingConfig';
import { computeShippingCost, COLOMBIA_CITIES } from '@/services/shipping';
import { fetchSalespersons, type Salesperson } from '@/services/salespersons';
import { getAgentSalespersonId } from '@/services/agent';
import { createSistecreditoDirectSale } from '@/services/sistecredito';

interface LineItem {
  variantId: string;
  quantity: string;
  // Si está marcado, este ítem queda exento del cobro de envío.
  exemptShipping: boolean;
}

interface SaleModalProps {
  onClose: () => void;
  onSaved: () => void;
}

export default function SaleModal({ onClose, onSaved }: SaleModalProps) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [variants, setVariants] = useState<VariantOption[]>([]);
  const [salespersons, setSalespersons] = useState<Salesperson[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [salespersonId, setSalespersonId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [lines, setLines] = useState<LineItem[]>([{ variantId: '', quantity: '1', exemptShipping: false }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [financing, setFinancing] = useState<{ orderNumber: string; link: string } | null>(null);

  // Envío con transportadora (Interrapidísimo).
  const shippingConfig = useShippingConfig();
  const [requiresShipping, setRequiresShipping] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientIdNumber, setRecipientIdNumber] = useState('');
  const [department, setDepartment] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  // Valor de envío editado manualmente por el vendedor. Vacío = cálculo automático.
  const [shippingOverride, setShippingOverride] = useState('');

  useEffect(() => {
    fetchCustomers().then((r) => {
      setCustomers(r.data);
      if (r.data[0]) setCustomerId(r.data[0].id);
    });
    fetchVariants().then((r) => setVariants(r.data));
    fetchSalespersons().then((r) => setSalespersons(r.data));
    getAgentSalespersonId().then((agentId) => {
      if (agentId) setSalespersonId(agentId);
    });
  }, []);

  const total = useMemo(
    () =>
      lines.reduce((sum, l) => {
        const v = variants.find((x) => x.id === l.variantId);
        return sum + (v ? v.retailPrice * l.quantity : 0);
      }, 0),
    [lines, variants],
  );

  // Solo los productos de línea SENCILLA cobran flete. La línea premium ya
  // incluye el envío en su precio, así que se puede despachar sin sumar costo.
  const cartChargesShipping = useMemo(
    () =>
      lines.some(
        (l) =>
          !l.exemptShipping &&
          variants.find((x) => x.id === l.variantId)?.chargeShipping === true,
      ),
    [lines, variants],
  );

  // Hay al menos un producto en el pedido (habilita el despacho con transportadora).
  const hasProducts = useMemo(() => lines.some((l) => l.variantId), [lines]);

  // Peso a cobrar: solo los ítems de línea sencilla NO eximidos suman flete.
  const chargeableWeight = useMemo(
    () =>
      lines.reduce((sum, l) => {
        if (l.exemptShipping) return sum;
        const v = variants.find((x) => x.id === l.variantId);
        if (!v || !v.chargeShipping) return sum;
        return sum + (v.weightKg ?? 0.5) * Math.max(1, Number(l.quantity) || 1);
      }, 0),
    [lines, variants],
  );

  // El flete solo se suma al total cuando hay babuchas SENCILLAS (línea que
  // cobra envío). En pedidos 100% premium el envío va incluido: se puede
  // despachar con transportadora, pero no suma costo.
  const shippingCost = !cartChargesShipping
    ? 0
    : requiresShipping && shippingOverride.trim() !== ''
      ? Number(shippingOverride) || 0
      : requiresShipping
        ? computeShippingCost(city, shippingConfig, chargeableWeight)
        : 0;
  const grandTotal = total + shippingCost;

  // Si el pedido se queda sin productos, apagamos el envío automáticamente.
  useEffect(() => {
    if (!hasProducts && requiresShipping) {
      setRequiresShipping(false);
    }
  }, [hasProducts, requiresShipping]);

  function toggleShipping() {
    if (!hasProducts) return;
    const next = !requiresShipping;
    setRequiresShipping(next);
    if (next && !recipientName.trim()) {
      const c = customers.find((x) => x.id === customerId);
      if (c) setRecipientName(`${c.firstName ?? ''} ${c.lastName ?? ''}`.trim());
    }
  }

  // Productos cuya cantidad facturada supera el stock disponible. Se usan para
  // avisar al vendedor que debe mandarlos a producción antes de confirmar la venta.
  const outOfStock = useMemo(
    () =>
      lines
        .filter((l) => l.variantId)
        .map((l) => {
          const v = variants.find((x) => x.id === l.variantId);
          if (!v) return null;
          const qty = Math.max(1, Number(l.quantity) || 1);
          if (qty <= v.available) return null;
          return {
            productName: v.productName,
            sizeName: v.sizeName,
            available: v.available,
            requested: qty,
          };
        })
        .filter(
          (x): x is { productName: string; sizeName: string; available: number; requested: number } =>
            x !== null,
        ),
    [lines, variants],
  );

  function updateLine(i: number, patch: Partial<LineItem>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((ls) => [...ls, { variantId: '', quantity: '1', exemptShipping: false }]);
  }
  function removeLine(i: number) {
    setLines((ls) => ls.filter((_, idx) => idx !== i));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!customerId) {
      setError('Selecciona un cliente.');
      return;
    }
    const items = lines
      .filter((l) => l.variantId)
      .map((l) => {
        const v = variants.find((x) => x.id === l.variantId)!;
        return {
          variantId: l.variantId,
          quantity: Math.max(1, Number(l.quantity) || 1),
          unitPrice: v.retailPrice,
          unitCost: v.unitCost,
        };
      });
    if (items.length === 0) {
      setError('Agrega al menos un producto.');
      return;
    }

    if (requiresShipping) {
      if (
        !recipientName.trim() ||
        !recipientPhone.trim() ||
        !recipientIdNumber.trim() ||
        !department.trim() ||
        !city.trim() ||
        !address.trim()
      ) {
        setError('Completa los datos de despacho (destinatario, teléfono, cédula, departamento, ciudad y dirección).');
        return;
      }
    }

    // Venta financiada con Sistecrédito: no descuenta inventario todavía. Se
    // crea el pedido con reserva y se genera el enlace de financiamiento; la
    // venta y el descuento se confirman cuando Sistecrédito apruebe (webhook).
    if (paymentMethod === 'sistecredito') {
      if (outOfStock.length > 0) {
        setError('Para financiar con Sistecrédito todos los productos deben tener stock disponible. Ajusta las cantidades o usa otro método de pago.');
        return;
      }
      await doSistecredito();
      return;
    }

    // Si hay productos sin stock, pedimos confirmación y recordamos que deben
    // mandarse a producción (la venta igual se puede facturar).
    if (outOfStock.length > 0) {
      setConfirming(true);
      return;
    }

    await doSave();
  }

  async function doSave() {
    const items = lines
      .filter((l) => l.variantId)
      .map((l) => {
        const v = variants.find((x) => x.id === l.variantId)!;
        return {
          variantId: l.variantId,
          quantity: Math.max(1, Number(l.quantity) || 1),
          unitPrice: v.retailPrice,
          unitCost: v.unitCost,
        };
      });

    setSaving(true);
    const shipping = requiresShipping
      ? {
          recipientName: recipientName.trim(),
          recipientPhone: recipientPhone.trim(),
          recipientIdNumber: recipientIdNumber.trim(),
          recipientEmail: recipientEmail.trim(),
          department: department.trim(),
          city: city.trim(),
          address: address.trim(),
          notes: deliveryNotes.trim(),
        }
      : undefined;
    const res = await createDirectSale({
      customerId,
      paymentMethod,
      items,
      salespersonId: salespersonId || undefined,
      shipping,
      shippingCostOverride: requiresShipping ? shippingCost : undefined,
    });
    setSaving(false);

    if (!res.success) {
      setError(res.error || 'No se pudo crear la venta.');
      setConfirming(false);
      return;
    }
    onSaved();
    onClose();
  }

  async function doSistecredito() {
    const items = lines
      .filter((l) => l.variantId)
      .map((l) => {
        const v = variants.find((x) => x.id === l.variantId)!;
        return {
          variantId: l.variantId,
          quantity: Math.max(1, Number(l.quantity) || 1),
          unitPrice: v.retailPrice,
          unitCost: v.unitCost,
        };
      });
    setSaving(true);
    setError('');
    try {
      const res = await createSistecreditoDirectSale({
        customerId,
        salespersonId: salespersonId || undefined,
        items,
        notes: '',
        shipping: requiresShipping
          ? {
              recipientName: recipientName.trim(),
              recipientPhone: recipientPhone.trim(),
              recipientIdNumber: recipientIdNumber.trim(),
              recipientEmail: recipientEmail.trim(),
              department: department.trim(),
              city: city.trim(),
              address: address.trim(),
              notes: deliveryNotes.trim(),
            }
          : undefined,
        shippingCost: requiresShipping ? shippingCost : 0,
      });
      setFinancing({ orderNumber: res.orderNumber, link: res.redirectUrl });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo iniciar el financiamiento.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Nueva Venta"
      subtitle="Crea una venta directa y descuenta el inventario."
      onClose={onClose}
      maxWidth="max-w-xl"
    >
      {financing ? (
        <div className="space-y-4">
          <div className="flex items-start gap-2.5 text-sm bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-md px-3 py-2.5">
            <i className="ri-checkbox-circle-line mt-0.5"></i>
            <div>
              <p className="font-semibold">Pedido de financiamiento creado</p>
              <p className="text-xs mt-0.5">
                Pedido <strong>{financing.orderNumber}</strong>. La venta y el descuento de
                inventario se confirmarán cuando Sistecrédito apruebe el crédito.
              </p>
            </div>
          </div>

          {financing.link ? (
            <>
              <p className="text-sm text-foreground-600">
                Comparte o abre este enlace para que el cliente complete su solicitud de crédito:
              </p>
              <div className="flex items-center gap-2">
                <input readOnly value={financing.link} className={inputCls} />
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard?.writeText(financing.link);
                  }}
                  className="px-3 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-file-copy-line mr-1"></i>Copiar
                </button>
              </div>
              <a
                href={financing.link}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-external-link-line"></i>Abrir checkout de Sistecrédito
              </a>
            </>
          ) : (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 flex items-start gap-2">
              <i className="ri-information-line mt-0.5"></i>
              <span>
                Sistecrédito recibió la solicitud pero todavía no devolvió su enlace de pago
                (enrolamiento pendiente). El pedido quedó registrado y podrás retomarlo desde
                Pedidos.
              </span>
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Cerrar
          </button>
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Cliente *</label>
            <select className={inputCls} value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.firstName} {c.lastName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Método de pago</label>
            <select className={inputCls} value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              <option value="cash">Contra entrega (efectivo)</option>
              <option value="nequi_negocios">Nequi</option>
              <option value="transfer">Transferencia</option>
              <option value="pse">PSE</option>
              <option value="card_credit">Tarjeta de crédito</option>
              <option value="card_debit">Tarjeta débito</option>
              <option value="sistecredito">Sistecrédito (compra a cuotas)</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Vendedor</label>
            <select className={inputCls} value={salespersonId} onChange={(e) => setSalespersonId(e.target.value)}>
              <option value="">Sin vendedor</option>
              {salespersons.map((sp) => (
                <option key={sp.id} value={sp.id}>
                  {sp.fullName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>Productos</label>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="border border-background-200 rounded-md p-2 space-y-1.5">
                <select
                  className={`${inputCls} w-full`}
                  value={l.variantId}
                  onChange={(e) => updateLine(i, { variantId: e.target.value })}
                >
                  <option value="">Selecciona variante...</option>
                  {variants.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.productName} · {v.sizeName} ({v.available} disp.)
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500 whitespace-nowrap">Cantidad</span>
                  <input
                    type="number"
                    min="1"
                    className={`${inputCls} w-24`}
                    value={l.quantity}
                    onChange={(e) => updateLine(i, { quantity: e.target.value })}
                  />
                  {lines.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      className="ml-auto w-8 h-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                      aria-label="Quitar"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  )}
                </div>
                {variants.find((x) => x.id === l.variantId)?.chargeShipping && (
                  <label className="flex items-center gap-2 text-xs text-foreground-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={l.exemptShipping}
                      onChange={(e) => updateLine(i, { exemptShipping: e.target.checked })}
                      className="w-3.5 h-3.5 accent-accent-500 cursor-pointer"
                    />
                    Eximir / omitir costo de envío de este ítem
                  </label>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addLine}
            className="mt-2 text-xs text-accent-600 hover:text-accent-700 font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1"></i>Agregar producto
          </button>
        </div>

        <div className={`border rounded-lg p-3.5 space-y-3 ${hasProducts ? 'border-background-200' : 'border-background-200 bg-background-50'}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <i className={`ri-truck-line text-lg ${hasProducts ? 'text-accent-600' : 'text-foreground-400'}`}></i>
              <div>
                <p className={`text-sm font-medium ${hasProducts ? 'text-foreground-900' : 'text-foreground-500'}`}>¿Deseas despachar con transportadora?</p>
                <p className="text-xs text-foreground-500">
                  {!hasProducts
                    ? 'Agrega al menos un producto para habilitar el envío.'
                    : cartChargesShipping
                      ? 'Interrapidísimo · suma el flete de las babuchas sencillas al total.'
                      : 'Interrapidísimo · babuchas premium: el envío va incluido, no suma costo.'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={toggleShipping}
              role="switch"
              aria-checked={requiresShipping}
              disabled={!hasProducts}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${hasProducts ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} ${requiresShipping ? 'bg-accent-500' : 'bg-background-300'}`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${requiresShipping ? 'translate-x-5' : ''}`}
              ></span>
            </button>
          </div>

          {requiresShipping && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div className="sm:col-span-2">
                <label className={labelCls}>Nombre destinatario *</label>
                <input
                  type="text"
                  className={inputCls}
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Nombre de quien recibe"
                />
              </div>
              <div>
                <label className={labelCls}>Celular *</label>
                <input
                  type="tel"
                  className={inputCls}
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  placeholder="+57 300 000 0000"
                />
              </div>
              <div>
                <label className={labelCls}>Cédula / NIT *</label>
                <input
                  type="text"
                  className={inputCls}
                  value={recipientIdNumber}
                  onChange={(e) => setRecipientIdNumber(e.target.value)}
                  placeholder="Ej: 1018452100"
                />
              </div>
              <div>
                <label className={labelCls}>Correo electrónico</label>
                <input
                  type="email"
                  className={inputCls}
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="correo@ejemplo.com"
                />
              </div>
              <div>
                <label className={labelCls}>Departamento *</label>
                <input
                  type="text"
                  className={inputCls}
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  placeholder="Ej: Cundinamarca"
                />
              </div>
              <div>
                <label className={labelCls}>Ciudad / Municipio *</label>
                <input
                  type="text"
                  list="pcp-sale-cities"
                  className={inputCls}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Ej: Bogotá"
                />
                <datalist id="pcp-sale-cities">
                  {COLOMBIA_CITIES.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Dirección completa *</label>
                <input
                  type="text"
                  className={inputCls}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Ej: Calle 123 #45-67, Apto 302, Torre B"
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Observaciones de entrega (torre, apto, piso, etc.)</label>
                <textarea
                  className={`${inputCls} resize-none`}
                  rows={2}
                  maxLength={500}
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  placeholder="Torre, apartamento, piso, etc. — Ej: Torre 2, Apto 302, Piso 3, portón negro"
                />
              </div>
              {cartChargesShipping ? (
                <div className="sm:col-span-2">
                  <label className={labelCls}>Costo de envío ($) — editable</label>
                  <input
                    type="number"
                    min="0"
                    className={inputCls}
                    value={shippingOverride !== '' ? shippingOverride : String(computeShippingCost(city, shippingConfig, chargeableWeight))}
                    onChange={(e) => setShippingOverride(e.target.value)}
                    placeholder="Valor del flete"
                  />
                  <p className="text-xs text-foreground-400 mt-1">
                    Sugerido automáticamente según la ciudad de destino. Puedes ajustarlo manualmente.
                  </p>
                </div>
              ) : (
                <div className="sm:col-span-2 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2.5 flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-emerald-600 mt-0.5"></i>
                  <p className="text-xs text-emerald-800 leading-relaxed">
                    <strong>Envío incluido</strong> para babuchas premium. Se genera la guía con transportadora, pero <strong>no se suma ningún costo</strong> al total.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {confirming && (
          <div className="border border-amber-300 bg-amber-50 rounded-md p-4 space-y-3">
            <div className="flex items-start gap-2.5">
              <i className="ri-alert-line text-amber-600 text-lg mt-0.5"></i>
              <div>
                <p className="text-sm font-semibold text-amber-800">Productos sin stock</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Los productos sin stock se enviarán automáticamente a producción (solo la cantidad que falta). La venta se factura de todas formas.
                </p>
              </div>
            </div>
            <ul className="space-y-1.5">
              {outOfStock.map((o, i) => (
                <li key={i} className="text-xs text-amber-800 flex items-center gap-1.5 flex-wrap">
                  <i className="ri-error-warning-line"></i>
                  <span className="font-medium">{o.productName}</span>
                  {o.sizeName && <span className="text-amber-700">· {o.sizeName}</span>}
                  <span className="text-amber-600">(disp. {o.available}, facturado {o.requested})</span>
                </li>
              ))}
            </ul>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="flex-1 px-4 py-2.5 text-sm border border-amber-300 text-amber-800 rounded-md hover:bg-amber-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Volver
              </button>
              <button
                type="button"
                onClick={doSave}
                disabled={saving}
                className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...
                  </>
                ) : (
                  <>Sí, facturar de todas formas</>
                )}
              </button>
            </div>
          </div>
        )}

        <div className="bg-accent-50 border border-accent-100 rounded-md px-4 py-3 space-y-1">
          {requiresShipping && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-600">Productos</span>
                <span className="text-foreground-800">${total.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-600">
                  Flete {cartChargesShipping && city ? `(${city})` : ''}
                </span>
                {cartChargesShipping ? (
                  <span className="text-foreground-800">
                    {city ? `$${shippingCost.toLocaleString()}` : 'Selecciona la ciudad'}
                  </span>
                ) : (
                  <span className="text-emerald-600 font-medium">Incluido</span>
                )}
              </div>
            </>
          )}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground-700">Total</span>
            <span className="text-lg font-bold text-foreground-950">${grandTotal.toLocaleString()}</span>
          </div>
        </div>

        {!confirming && (
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-1.5"></i>Crear venta
                </>
              )}
            </button>
          </div>
        )}
      </form>
      )}
    </Modal>
  );
}